import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import SignatureCanvas from 'react-signature-canvas';
import {
  ArrowLeftIcon,
  BeakerIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  LockClosedIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const MANAGE_ROLES = [
  'super_admin', 'admin', 'office_admin', 'fabrication_manager',
  'shop_foreman', 'millwright_foreman', 'foreman',
  'project_manager', 'project_manager_estimator'
];

const etNowTime = () =>
  new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'America/Toronto' });

const fmtTime = (hhmm) => {
  if (!hhmm) return '--';
  try {
    return new Date(`2000-01-01T${hhmm}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch { return hhmm; }
};

const ConfinedSpacePermit = () => {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [permit, setPermit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // Add-test form
  const [showTestForm, setShowTestForm] = useState(false);
  const [test, setTest] = useState({ test_time: '', tester: '', o2: '', lel: '', h2s: '', co: '', notes: '' });
  const [savingTest, setSavingTest] = useState(false);

  // Close-out
  const [showCloseOut, setShowCloseOut] = useState(false);
  const [closeTime, setCloseTime] = useState('');
  const [remarks, setRemarks] = useState('');
  const [supervisorSig, setSupervisorSig] = useState(null);
  const [entrants, setEntrants] = useState([]);
  const [closing, setClosing] = useState(false);

  // Shared signature modal: 'supervisor' | entrant index | null
  const [signingFor, setSigningFor] = useState(null);
  const sigRef = useRef(null);

  const loadPermit = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/inspections/${inspectionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setPermit(await res.json());
    } catch (e) {
      console.error('Failed to load permit:', e);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, inspectionId]);

  useEffect(() => { loadPermit(); }, [loadPermit]);

  const itemNote = (needle) => {
    const it = (permit?.inspection_items || []).find(i => (i.description || '').toLowerCase().includes(needle));
    return it?.notes || '';
  };

  const isOpen = permit?.permit_status === 'open';
  const canManage = !!user && (user.id === permit?.inspector_id || MANAGE_ROLES.includes(user.role));
  const tests = permit?.atmospheric_tests || [];

  const openTestForm = () => {
    setTest({ test_time: etNowTime(), tester: user?.full_name || '', o2: '', lel: '', h2s: '', co: '', notes: '' });
    setShowTestForm(true);
  };

  const submitTest = async () => {
    if (!test.o2 && !test.lel && !test.h2s && !test.co) {
      alert('Enter at least one gas reading (O2, LEL, H2S or CO).');
      return;
    }
    setSavingTest(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/inspections/${inspectionId}/atmospheric-test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(test)
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setPermit(prev => ({ ...prev, atmospheric_tests: [...(prev.atmospheric_tests || []), data.test] }));
        setShowTestForm(false);
      } else {
        alert(data.detail || 'Could not add the test.');
      }
    } catch (e) {
      alert('Could not add the test.');
    } finally {
      setSavingTest(false);
    }
  };

  const openCloseOut = () => {
    const names = itemNote('authorized entrant')
      .split(/[\n,;]+/).map(n => n.trim()).filter(Boolean);
    setEntrants((names.length > 0 ? names : ['']).map(n => ({ name: n, signature: null })));
    setCloseTime(etNowTime());
    setRemarks('');
    setSupervisorSig(null);
    setShowCloseOut(true);
  };

  const saveSignature = () => {
    if (!sigRef.current || sigRef.current.isEmpty()) { alert('Please sign before saving.'); return; }
    const dataUrl = sigRef.current.toDataURL();
    if (signingFor === 'supervisor') {
      setSupervisorSig(dataUrl);
    } else {
      setEntrants(prev => prev.map((e, i) => i === signingFor ? { ...e, signature: dataUrl } : e));
    }
    setSigningFor(null);
  };

  const submitCloseOut = async () => {
    if (!supervisorSig) { alert('❌ The entry supervisor must sign to close the permit.'); return; }
    const filled = entrants.filter(e => e.name.trim());
    if (filled.length === 0) { alert('❌ Add at least one entrant with a name.'); return; }
    const unsigned = filled.filter(e => !e.signature);
    if (unsigned.length > 0) {
      alert(`❌ All entrants must sign. Missing signature(s): ${unsigned.map(e => e.name).join(', ')}`);
      return;
    }
    setClosing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/inspections/${inspectionId}/close-permit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          close_time: closeTime,
          remarks,
          supervisor_signature: supervisorSig,
          entrant_signatures: filled.map(e => ({ name: e.name.trim(), signature: e.signature }))
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        alert(`✅ PERMIT CLOSED\n\nThe confined space permit is closed out and the final PDF has been generated${data.google_drive_link ? ' and filed to Google Drive' : ''}.`);
        navigate('/mobile/inspections');
      } else {
        alert(data.detail || 'Could not close the permit.');
      }
    } catch (e) {
      alert('Could not close the permit.');
    } finally {
      setClosing(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/inspections/${inspectionId}/download-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) { alert('Could not generate the PDF.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!permit) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6 text-center">
        <div>
          <p className="text-red-600 font-medium">Permit not found</p>
          <button onClick={() => navigate('/mobile/inspections')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg">
            Back to Inspections
          </button>
        </div>
      </div>
    );
  }

  const spaceDesc = itemNote('confined space id');

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className={`${isOpen ? 'bg-amber-600' : 'bg-slate-700'} text-white p-4 shadow-lg`}>
        <div className="flex items-center">
          <button onClick={() => navigate('/mobile/inspections')} className="mr-3" data-testid="permit-back-btn">
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Confined Space Permit</h1>
            <p className={`text-sm ${isOpen ? 'text-amber-800' : 'text-slate-800'}`}>{spaceDesc || 'Confined space entry'}</p>
          </div>
          <span
            data-testid="permit-status-badge"
            className={`px-3 py-1 rounded-full text-xs font-bold ${isOpen ? 'bg-emerald-500 text-white animate-pulse' : 'bg-white text-slate-800'}`}
          >
            {isOpen ? 'OPEN' : 'CLOSED'}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Permit info */}
        <div className="bg-white rounded-lg p-4 shadow-sm" data-testid="permit-info-card">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold">Work Order</p>
              <p className="text-gray-900 font-medium">{permit.work_order_number || '--'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold">Job Site</p>
              <p className="text-gray-900 font-medium">{permit.job_site || itemNote('site / client') || '--'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold">Opened By</p>
              <p className="text-gray-900 font-medium">{permit.opened_by_name || permit.inspector_name}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-semibold">Opened</p>
              <p className="text-gray-900 font-medium">{permit.inspection_date} {fmtTime(permit.inspection_time)}</p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/mobile/inspection/confined_space_permit/${inspectionId}`)}
            className="mt-3 text-sm text-blue-600 font-medium flex items-center"
            data-testid="view-permit-form-link"
          >
            <DocumentTextIcon className="h-4 w-4 mr-1" /> View full permit form
          </button>
        </div>

        {/* Atmospheric test log */}
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center">
              <BeakerIcon className="h-5 w-5 mr-2 text-amber-600" /> Atmospheric Test Log
            </h2>
            {isOpen && canManage && !showTestForm && (
              <button
                onClick={openTestForm}
                className="flex items-center px-3 py-1.5 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700"
                data-testid="add-test-btn"
              >
                <PlusIcon className="h-4 w-4 mr-1" /> Add Test
              </button>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-3">The opening readings are on the permit form — log every re-test here throughout the entry.</p>

          {tests.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4" data-testid="no-tests-msg">No additional tests recorded yet.</p>
          ) : (
            <div className="space-y-2" data-testid="test-log-list">
              {tests.map((t, i) => (
                <div key={t.id || i} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-800 flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1 text-gray-500" /> {fmtTime(t.test_time)}
                    </span>
                    <span className="text-xs text-gray-500">{t.tester}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    {[['O2 %', t.o2], ['LEL %', t.lel], ['H2S ppm', t.h2s], ['CO ppm', t.co]].map(([label, val]) => (
                      <div key={label} className="bg-white rounded border border-gray-200 py-1.5">
                        <p className="text-[10px] text-gray-500 font-semibold">{label}</p>
                        <p className="text-sm font-bold text-gray-900">{val || '--'}</p>
                      </div>
                    ))}
                  </div>
                  {t.notes && <p className="text-xs text-gray-600 mt-2 italic">{t.notes}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Add test form */}
          {showTestForm && (
            <div className="mt-4 border-2 border-amber-300 rounded-lg p-3 bg-amber-50" data-testid="add-test-form">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-amber-900 text-sm">New Atmospheric Test</h3>
                <button onClick={() => setShowTestForm(false)}><XMarkIcon className="h-5 w-5 text-amber-700" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Time of test</label>
                  <input type="time" value={test.test_time} onChange={e => setTest({ ...test, test_time: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded text-gray-900" data-testid="test-time-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Tester</label>
                  <input type="text" value={test.tester} onChange={e => setTest({ ...test, tester: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded text-gray-900" data-testid="test-tester-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">O2 % (19.5–23.0)</label>
                  <input type="text" inputMode="decimal" value={test.o2} onChange={e => setTest({ ...test, o2: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded text-gray-900" placeholder="20.9" data-testid="test-o2-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">LEL % (&lt; 10)</label>
                  <input type="text" inputMode="decimal" value={test.lel} onChange={e => setTest({ ...test, lel: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded text-gray-900" placeholder="0" data-testid="test-lel-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">H2S ppm</label>
                  <input type="text" inputMode="decimal" value={test.h2s} onChange={e => setTest({ ...test, h2s: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded text-gray-900" placeholder="0" data-testid="test-h2s-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">CO ppm</label>
                  <input type="text" inputMode="decimal" value={test.co} onChange={e => setTest({ ...test, co: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded text-gray-900" placeholder="0" data-testid="test-co-input" />
                </div>
              </div>
              <input type="text" value={test.notes} onChange={e => setTest({ ...test, notes: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded text-gray-900 mb-3" placeholder="Notes (optional)" data-testid="test-notes-input" />
              <button
                onClick={submitTest}
                disabled={savingTest}
                className="w-full py-2.5 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 disabled:opacity-50"
                data-testid="submit-test-btn"
              >
                {savingTest ? 'Saving…' : 'Save Test Reading'}
              </button>
            </div>
          )}
        </div>

        {/* Access-control note */}
        {isOpen && !canManage && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800" data-testid="permit-readonly-note">
            Only the entry supervisor who opened this permit, or a foreman/manager, can add tests or close it out.
          </div>
        )}

        {/* Closed record */}
        {!isOpen && (
          <div className="bg-white rounded-lg p-4 shadow-sm" data-testid="permit-closed-record">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center mb-2">
              <LockClosedIcon className="h-5 w-5 mr-2 text-slate-600" /> Permit Close-Out Record
            </h2>
            <p className="text-sm text-gray-700">
              Closed by <span className="font-semibold">{permit.permit_closed_by}</span> on {permit.permit_closed_date} at {fmtTime(permit.permit_closed_time)}.
            </p>
            {permit.permit_close_remarks && (
              <p className="text-sm text-gray-600 mt-1 italic">"{permit.permit_close_remarks}"</p>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Signed by {(permit.worker_signatures || []).length} entrant(s) + entry supervisor.
            </p>
            <button
              onClick={downloadPdf}
              disabled={downloading}
              className="mt-3 w-full flex items-center justify-center py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50"
              data-testid="permit-download-pdf-btn"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" /> {downloading ? 'Generating…' : 'Download Permit PDF'}
            </button>
          </div>
        )}

        {/* Close-out */}
        {isOpen && canManage && !showCloseOut && (
          <button
            onClick={openCloseOut}
            className="w-full py-4 bg-red-600 text-white rounded-lg font-bold shadow-lg hover:bg-red-700 flex items-center justify-center"
            data-testid="close-permit-btn"
          >
            <LockClosedIcon className="h-5 w-5 mr-2" /> Close Out Permit
          </button>
        )}

        {isOpen && canManage && showCloseOut && (
          <div className="bg-white rounded-lg p-4 shadow-sm border-2 border-red-300" data-testid="close-out-section">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-red-700 flex items-center">
                <LockClosedIcon className="h-5 w-5 mr-2" /> Close Out Permit
              </h2>
              <button onClick={() => setShowCloseOut(false)}><XMarkIcon className="h-5 w-5 text-gray-500" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Access complete — record the close time, get the entry supervisor and every entrant to sign, then close. The final PDF is generated on close-out.</p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Close-out time</label>
                <input type="time" value={closeTime} onChange={e => setCloseTime(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-gray-900" data-testid="close-time-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (optional)</label>
                <textarea rows={2} value={remarks} onChange={e => setRemarks(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-gray-900"
                  placeholder="e.g. All personnel and equipment out of the space, ventilation removed"
                  data-testid="close-remarks-input" />
              </div>

              {/* Supervisor signature */}
              <div className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Entry Supervisor Signature <span className="text-red-600">*</span></p>
                    <p className="text-xs text-gray-500">{permit.opened_by_name || permit.inspector_name}</p>
                  </div>
                  {supervisorSig ? (
                    <span className="flex items-center text-emerald-600 text-sm font-medium">
                      <CheckCircleIcon className="h-5 w-5 mr-1" /> Signed
                    </span>
                  ) : (
                    <button
                      onClick={() => setSigningFor('supervisor')}
                      className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium"
                      data-testid="supervisor-sign-btn"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" /> Sign
                    </button>
                  )}
                </div>
                {supervisorSig && <img src={supervisorSig} alt="Supervisor signature" className="mt-2 h-14 border border-gray-200 rounded bg-white" />}
              </div>

              {/* Entrant signatures */}
              <div className="border border-gray-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-gray-800 mb-2">Entrant Signatures <span className="text-red-600">* all entrants</span></p>
                <div className="space-y-2">
                  {entrants.map((e, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={e.name}
                        onChange={ev => setEntrants(prev => prev.map((x, j) => j === i ? { ...x, name: ev.target.value } : x))}
                        className="flex-1 p-2 border border-gray-300 rounded text-gray-900 text-sm"
                        placeholder="Entrant name"
                        data-testid={`entrant-name-input-${i}`}
                      />
                      {e.signature ? (
                        <span className="flex items-center text-emerald-600 text-xs font-medium whitespace-nowrap">
                          <CheckCircleIcon className="h-4 w-4 mr-0.5" /> Signed
                        </span>
                      ) : (
                        <button
                          onClick={() => { if (!e.name.trim()) { alert('Enter the entrant name first.'); return; } setSigningFor(i); }}
                          className="px-2.5 py-1.5 bg-blue-600 text-white rounded text-xs font-medium whitespace-nowrap"
                          data-testid={`entrant-sign-btn-${i}`}
                        >
                          Sign
                        </button>
                      )}
                      <button onClick={() => setEntrants(prev => prev.filter((_, j) => j !== i))} data-testid={`entrant-remove-btn-${i}`}>
                        <TrashIcon className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setEntrants(prev => [...prev, { name: '', signature: null }])}
                  className="mt-2 flex items-center text-sm text-blue-600 font-medium"
                  data-testid="add-entrant-btn"
                >
                  <PlusIcon className="h-4 w-4 mr-1" /> Add entrant
                </button>
              </div>

              <button
                onClick={submitCloseOut}
                disabled={closing}
                className="w-full py-3.5 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-close-permit-btn"
              >
                {closing ? 'Closing permit & generating PDF…' : 'Confirm Close-Out & Generate PDF'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Signature modal */}
      {signingFor !== null && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="signature-modal">
          <div className="bg-white rounded-xl w-full max-w-md p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-900">
                {signingFor === 'supervisor' ? 'Entry Supervisor Signature' : `Signature — ${entrants[signingFor]?.name || 'Entrant'}`}
              </h3>
              <button onClick={() => setSigningFor(null)}><XMarkIcon className="h-5 w-5 text-gray-500" /></button>
            </div>
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
              <SignatureCanvas
                ref={sigRef}
                canvasProps={{ className: 'w-full h-40', style: { touchAction: 'none' } }}
                backgroundColor="white"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1 flex items-center"><PencilIcon className="h-3.5 w-3.5 mr-1" /> Sign with your finger or stylus</p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={() => sigRef.current && sigRef.current.clear()}
                className="flex-1 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium"
                data-testid="signature-clear-btn"
              >
                Clear
              </button>
              <button
                onClick={saveSignature}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg font-semibold"
                data-testid="signature-save-btn"
              >
                Save Signature
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="h-20"></div>
    </div>
  );
};

export default ConfinedSpacePermit;
